import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import { AppModule } from '@/store/modules/app';
import { Languages } from '@/types/languagesType';
import DefaultTemplate from '@/layouts/DefaultTemplate.vue';
import CabinetLayout from '@/layouts/CabinetLayout.vue';
import Welcome from '@/views/WelcomeView.vue';
import { UserModule } from '@/store/modules/user';
import { ThemesModule } from '@/store/modules/themes';
import { getByDomain } from '@/config/sites';
import { UserType } from '@/types/ServerToken';
import { AnalysisType } from '@/types/Analysis';

// Old /jobs?tab=… links (tabs inside one page) → the Arbeit pages
const LEGACY_JOBS_TABS: Record<string, string> = {
  suche: 'Jobs',
  katalog: 'JobsCatalog',
  ausbildung: 'JobsTraining',
  orte: 'JobsLocations',
  company: 'JobsCompanies',
  map: 'JobsMap',
};

// Old /workers?tab=… links → the Personal pages
const LEGACY_WORKERS_TABS: Record<string, string> = {
  suche: 'Workers',
  katalog: 'WorkersCatalog',
};

// Old /courses?tab=katalog links → the Bildung catalog
const LEGACY_COURSES_TABS: Record<string, string> = {
  katalog: 'CoursesCatalog',
};

Vue.use(VueRouter);

// maps the `cabinet` query param to the internal user type
const CABINET_TYPE_MAP: Record<string, number> = {
  applicant: UserType.applicant,
  consultant: UserType.consultant,
  company: UserType.company,
};

const routes: Array<RouteConfig> = [
  {
    path: '',
    component: DefaultTemplate,
    children: [
      {
        path: '/',
        name: 'Welcome',
        component: Welcome,
        // meta.talentspace: page is redesigned and renders full width; meta.pageClass: design page class (see DefaultTemplate)
        meta: { talentspace: true, pageClass: 'talentspace-welcome-page' },
      },
      // Short links to one job ad / profile / course (sections/shareLinks.ts): the section page
      // with the panel open. Numbers only, so /jobs/results etc. are not caught.
      {
        path: '/jobs/:id(\\d+)',
        redirect: (to) => ({ name: 'Jobs', query: { ...to.query, jobViewId: to.params.id } }),
      },
      {
        path: '/workers/:id(\\d+)',
        redirect: (to) => ({ name: 'Workers', query: { ...to.query, workerViewId: to.params.id } }),
      },
      {
        path: '/cv/:id(\\d+)',
        redirect: (to) => ({ name: 'Workers', query: { ...to.query, workerViewId: to.params.id } }),
      },
      {
        path: '/courses/:id(\\d+)',
        redirect: (to) => ({ name: 'Courses', query: { ...to.query, courseViewId: to.params.id } }),
      },
      {
        // Arbeit section: the parent keeps the search state and the URL in step (see JobsView);
        // meta.section shows the section tabs, meta.analysis is the page type for the statistics
        path: '/jobs',
        component: () => import('../views/Jobs/JobsView.vue'),
        children: [
          {
            // absolute child paths: a '' child would give the section a trailing slash (/jobs/)
            path: '/jobs',
            name: 'Jobs',
            component: () => import('../views/Jobs/JobsSearchView.vue'),
            meta: { talentspace: true, section: 'jobs', analysis: AnalysisType.PORTAL_JOBS },
          },
          {
            path: '/jobs/results',
            name: 'JobsResults',
            component: () => import('../views/Jobs/JobsResultsView.vue'),
            meta: { talentspace: true, section: 'jobs', analysis: AnalysisType.PORTAL_JOBS },
          },
          {
            path: '/jobs/catalog',
            name: 'JobsCatalog',
            component: () => import('../views/Jobs/JobsCatalogView.vue'),
            meta: { talentspace: true, section: 'jobs', analysis: AnalysisType.PORTAL_JOBS_CATALOG },
          },
          // Extra tabs of some sites (PAT counters), not redesigned yet
          {
            path: '/jobs/training',
            name: 'JobsTraining',
            component: () => import('../views/AusbildungView.vue'),
            meta: { section: 'jobs', analysis: AnalysisType.PORTAL_JOBS_EDUCATION },
          },
          {
            path: '/jobs/locations',
            name: 'JobsLocations',
            component: () => import('../views/OrteView.vue'),
            meta: { section: 'jobs', analysis: AnalysisType.PORTAL_JOBS_LOCATION },
          },
          {
            path: '/jobs/companies',
            name: 'JobsCompanies',
            component: () => import('../views/CompanyView.vue'),
            meta: { section: 'jobs', analysis: AnalysisType.PORTAL_JOBS_FIRMS },
          },
          {
            path: '/jobs/map',
            name: 'JobsMap',
            component: () => import('../views/MapView.vue'),
            meta: { section: 'jobs', analysis: AnalysisType.PORTAL_JOBS_MAP },
          },
        ],
      },
      {
        // Personal section: as Arbeit, the parent keeps the search state and the pages in step
        path: '/workers',
        component: () => import('../views/Workers/WorkersView.vue'),
        children: [
          {
            path: '/workers',
            name: 'Workers',
            component: () => import('../views/Workers/WorkersSearchView.vue'),
            meta: { talentspace: true, section: 'workers', analysis: AnalysisType.PORTAL_PERSONAL },
          },
          {
            path: '/workers/results',
            name: 'WorkersResults',
            component: () => import('../views/Workers/WorkersResultsView.vue'),
            meta: { talentspace: true, section: 'workers', analysis: AnalysisType.PORTAL_PERSONAL },
          },
          {
            path: '/workers/catalog',
            name: 'WorkersCatalog',
            component: () => import('../views/Workers/WorkersCatalogView.vue'),
            meta: { talentspace: true, section: 'workers', analysis: AnalysisType.PORTAL_PERSONAL_CATALOG },
          },
        ],
      },
      {
        path: '/company',
        redirect: { name: 'JobsCompanies' },
      },
      {
        path: '/map',
        redirect: { name: 'JobsMap' },
      },
      {
        path: '/impressum',
        name: 'Impressum',
        component: () => import('../views/ImpressumView.vue'),
      },
      {
        path: '/iframe-workers',
        name: 'WorkersIframe',
        component: () => import('../views/Iframe/WorkersIframeView.vue'),
      },
      {
        // Bildung section: as Arbeit, the parent keeps the search state and the pages in step
        path: '/courses',
        component: () => import('../views/Courses/CoursesView.vue'),
        children: [
          {
            path: '/courses',
            name: 'Courses',
            component: () => import('../views/Courses/CoursesSearchView.vue'),
            meta: { talentspace: true, section: 'courses', analysis: AnalysisType.PORTAL_KURSES },
          },
          {
            path: '/courses/results',
            name: 'CoursesResults',
            component: () => import('../views/Courses/CoursesResultsView.vue'),
            meta: { talentspace: true, section: 'courses', analysis: AnalysisType.PORTAL_KURSES },
          },
          {
            path: '/courses/catalog',
            name: 'CoursesCatalog',
            component: () => import('../views/Courses/CoursesCatalogView.vue'),
            meta: { talentspace: true, section: 'courses', analysis: AnalysisType.PORTAL_KURSES_CATALOG },
          },
        ],
      },
      {
        path: '/ausbildung',
        redirect: { name: 'JobsTraining' },
      },
      {
        path: '/orte',
        redirect: { name: 'JobsLocations' },
      },
      {
        path: '/confirm_email',
        name: 'confirmEmail',
        component: () => import('../views/ConfirmEmail.vue'),
      },
      {
        path: '/login',
        name: 'LoginLegacy',
        component: Welcome,
        meta: { talentspace: true, pageClass: 'talentspace-welcome-page' },
      },
    ],
  },
  {
    // isolated standalone page — no app header/footer/sidebars
    path: '/stat',
    name: 'Stat',
    component: () => import('../views/StatView.vue'),
  },
  {
    path: '',
    component: CabinetLayout,
    children: [
      {
        path: '/cabinet',
        name: 'Cabinet',
        component: () => import('../views/Profile/CabinetEntryView.vue'),
      },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  // A new page starts at its top, back/forward returns to where it was; a changed query
  // (page of a list, filters) keeps the place — the list scrolls itself
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.path !== from.path) return { x: 0, y: 0 };
    return undefined;
  },
});

router.beforeEach(async(to, from, next) => {
  const legacyAuthHash = to.hash.replace('#', '');
  if (
    to.path === '/login'
    && (legacyAuthHash === 'a-login' || legacyAuthHash === 'f-login')
    && !to.query.auth
  ) {
    next({
      path: '/',
      query: {
        ...to.query,
        auth: legacyAuthHash,
      },
      replace: true,
    });
    return;
  }

  if (to.path === '/jobs' && to.query.tab) {
    const { tab, ...query } = to.query;
    next({ name: LEGACY_JOBS_TABS[String(tab)] || 'Jobs', query, replace: true });
    return;
  }

  if (to.path === '/workers' && to.query.tab) {
    const { tab, ...query } = to.query;
    next({ name: LEGACY_WORKERS_TABS[String(tab)] || 'Workers', query, replace: true });
    return;
  }

  if (to.path === '/courses' && to.query.tab) {
    const { tab, ...query } = to.query;
    next({ name: LEGACY_COURSES_TABS[String(tab)] || 'Courses', query, replace: true });
    return;
  }

  const ThemeDetection = getByDomain();

  if (ThemeDetection) {
    AppModule.ActionSid(ThemeDetection.sid);

    // add theme
    ThemesModule.SetTheme(ThemeDetection);
  }

  // language query redirect
  if (to.query.lang && Object.values(Languages).includes(to.query.lang as Languages)) {
    AppModule.ActionLanguage(String(to.query.lang) as Languages);
  } else {
    // if (ThemeDetection && ThemeDetection.language) {
    //   AppModule.ActionLanguage(ThemeDetection.language);
    // }
    // @ts-ignore
    next({ ...to, query: { ...to.query, lang: AppModule.language }, replace: true });
    return;
  }

  // auto-login via deep link: /?access_token={token}&cabinet=applicant|consultant|company
  if (to.path === '/' && to.query.access_token && to.query.cabinet) {
    const cabinet = String(to.query.cabinet);
    const type = CABINET_TYPE_MAP[cabinet];
    if (type !== undefined) {
      try {
        await UserModule.LoginBySingleUseToken({
          single_use_token: String(to.query.access_token),
          sid: AppModule.sid,
          type,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('single_use_token login error:', e);
      }
      // strip the token from the URL so it is not exposed in the address bar / history
      const {
        access_token, cabinet: _cabinet, refresh_token, expires, ...restQuery
      } = to.query;
      next({ path: '/', query: { ...restQuery, lang: AppModule.language }, replace: true });
      return;
    }
  }

  if (!UserModule.accessTokenApplicant && !UserModule.accessTokenConsultant && !UserModule.accessTokenCompany) {
    if (to.path === '/cabinet') {
      next({ path: '/' });
    }
  }

  if (to.query.iframe) {
    AppModule.SetIsIframe(true);
  }

  if (to.query.sid) {
    AppModule.ActionLanguageSid(String(to.query.sid));
  }
  next();
});

export default router;
